import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleFaqBodyStyle,
  articleFigureCaption,
  articleImageStyle,
  bold,
  faqGrid,
  faqH3Style,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  infoWrap,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { vars } from "~/components/styles/theme.css"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"

export const meta: MetaFunction = () => {
  return [
    {
      title: "Contemporary Garden Design | Materials and Plants",
    },
    {
      name: "description",
      content:
        "Discover what makes up a contemporary garden with our breakdown of key materials and plants that define the style.",
    },
    {
      property: "og:title",
      content: "Contemporary Garden Design | Materials and Plants",
    },
    {
      property: "og:description",
      content:
        "Discover what makes up a contemporary garden with our breakdown of key materials and plants that define the style.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/article/modern-garden.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-guide/contemporary/materials-and-plants",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Contemporary Garden Design | Materials and Plants",
        description:
          "Discover what makes up a contemporary garden with our breakdown of key materials and plants that define the style.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-20",
        dateModified: "2025-10-20",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/article/modern-garden.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id":
            "https://rescapeai.com/garden-design-guide/contemporary/materials-and-plants",
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/landing/modern-garden.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Contemporary Garden Design Ideas: Clean Lines, Minimal Materials & Modern
            Planting
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Contemporary gardens feature clean sharp lines and incorporate modern and
            minimal structures and materials. The style works for large or small gardens
            and traditional structures such as water fountains can take centre stage and
            boast a sleak modern look. Reducing materials and colours is essential to
            creating the clean contemporary garden design so let’s take a look at these
            modern gardens to help you create a style that you’ll love.
          </p>
          <p className={articleBodyStyle}>
            Bold straight lines and geometric layouts create the sharp look typical of
            contemporary gardens. A great place to start for this look is in the fence
            panels bordering your garden. Slotted panels come in different materials and
            stains to fit your own preference. They are a great backdrop for the rest of
            the garden and pair well with small to medium plants and bushes. Stick to
            black, grey or natural wood for these backdrops as not to distract from this
            minimal style.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <figure>
            <img
              src={"/guide/contemporary-dark-fence.webp"}
              alt={"contemporary dark fence"}
              className={articleImageStyle}
              loading="lazy"
            />
            <figcaption className={articleFigureCaption}>
              A dark grey contemporary slatted fence
            </figcaption>
          </figure>
          <figure>
            <img
              src={"/guide/contemporary-timber-fence.jpg"}
              alt={"contemporary timber fence"}
              className={articleImageStyle}
              loading="lazy"
            />
            <figcaption className={articleFigureCaption}>
              A Contemporary timber slatted fence
            </figcaption>
          </figure>
        </section>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Choosing contemporary style doesn't mean everything needs straight edges.
            Rounded shapes—like decorative spheres, large round pots, or circular lawns
            and pathways—work beautifully in larger gardens. While winding paths don't
            suit contemporary design, circles can still play a role. Notice how the round
            grass area in this image contrasts with the garden fence while maintaining a
            modern look.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <figure>
            <img
              src={"/guide/modern-garden.webp"}
              alt={"contemporary garden with round features"}
              className={articleImageStyle}
              loading="lazy"
            />
            <figcaption className={articleFigureCaption}>
              A design incorporating a circle grass area and spherical ornaments
            </figcaption>
          </figure>
        </section>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Concrete or painted concrete and wood can be paired well and balance one
            another creating a modern feel. Seating areas in contemporary spaces usually
            conform to the straight lines and edges talked about earlier so don’t mix in a
            round table and chairs, aim for creating something that blends with the space.
          </p>
          <p className={articleBodyStyle}>
            <Link href="/article/contemporary-garden-ideas-with-rescape-ai">
              A pergola is the perfect area to create sheltered space within your garden.
            </Link>{" "}
            Often paired with comfortable cotton seating it becomes a space of relaxation.
            Pergolas can also be bought in many materials to match the rest of your garden
            and you can grow climbing plants up and across them to create shelter if you
            choose a wooden slatted pergola.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <figure>
            <img
              src={
                "/how-to-design-a-contemporary-garden/third-revision-ai-generated-garden.webp"
              }
              alt={
                "contemporary garden design with pergola and seating generated with ai"
              }
              className={articleImageStyle}
              loading="lazy"
            />
            <figcaption className={articleFigureCaption}>
              Contemporary garden layout generated with Rescape AI
            </figcaption>
          </figure>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Plant choices for contemporary gardens</h2>
          <p className={articleBodyStyle}>
            When choosing plants for your contemporary garden there’s two options
            recommended before you get started. Choose a limited amount of plants, adding
            the the architectural interest of the garden. Or plant densely with large
            leaved foliage and grasses to add a naturalistic feel to the space while
            keeping it contemporary.
          </p>
          <p className={articleBodyStyle}>
            Keep it no more than three colours in your plant colour choices to stick to
            the contemporary design principles. Too many colours will make it feel wild
            and unstructured and not contemporary.
          </p>
          <div className={infoWrap}>
            <h3 className={h3Style}>Bamboo</h3>
            <img
              src={"/guide/bamboo-in-garden.webp"}
              alt={"Bamboo uses in contemporary garden"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Bamboo is a timeless choice for contemporary gardens, bringing vertical
              interest and a sense of calm to modern outdoor spaces. Its tall, slender
              canes create natural screening that's perfect for defining boundaries or
              adding privacy. The gentle rustling of bamboo leaves in the breeze adds a
              subtle sensory element. Choose clumping varieties to prevent spreading, and
              enjoy year-round structure, privacy, and a touch of the exotic.
            </p>
          </div>
          <div className={infoWrap}>
            <h3 className={h3Style}>Echinacea</h3>
            <img
              src={"/guide/echinacea.webp"}
              alt={"Echinacea used in Contemporary garden"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Echinacea, brings modern elegance to contemporary gardens with its bold,
              architectural blooms and clean lines. Its sculptural petals and striking
              centers pair beautifully with ornamental grasses and minimalist planting
              schemes. Drought-tolerant and pollinator-friendly, echinacea offers both
              resilience and artistry—perfect for low-maintenance, design-forward
              landscapes that value form and function.
            </p>
          </div>
          <div className={infoWrap}>
            <h3 className={h3Style}>Grasses</h3>
            <img
              src={"/guide/grasses.webp"}
              alt={"contemporary garden with grasses"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Grasses are a staple in contemporary garden design, offering movement,
              texture, and structure that soften sharp architectural lines. Their subtle
              tones of green, gold, and silver complement modern materials like concrete
              and timber, creating a calm and balanced aesthetic. Grasses thrive in
              minimalist schemes where fewer colours and clean shapes dominate. They
              require little maintenance and move gracefully in the wind, bringing life to
              even the most structured layouts. Whether planted in sleek raised beds or
              used to frame modern sculptures, ornamental grasses blend natural beauty
              with contemporary sophistication.
            </p>
          </div>
          <div className={infoWrap}>
            <h3 className={h3Style}>Architectural plants</h3>
            <img
              src={"/guide/architectural-plant.webp"}
              alt={"Architectural plants used in Contemporary gardening"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Architectural plants are the statement pieces of contemporary gardens—bold,
              sculptural, and perfectly suited to clean modern spaces. Their strong forms
              and striking silhouettes bring structure and year-round interest, making
              them ideal focal points against minimalist hardscaping like concrete, slate,
              or timber.
            </p>
            <p className={articleBodyStyle}>
              Think of plants such as Phormium, Yucca, Agave, or Fatsia japonica—each
              offering distinct shapes that command attention. These plants thrive on
              simplicity, often standing alone or grouped in threes for maximum impact.
              Pair them with gravel, smooth paving, or sleek planters to highlight their
              dramatic presence.
            </p>
          </div>
          <div className={infoWrap}>
            <h3 className={h3Style}>
              Seasonal Flowering Plants for Contemporary Gardens
            </h3>
            <img
              src={"/guide/seasonal-flowering-plants.webp"}
              alt={"Seasonal flowering plants in contemporary gardening"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Seasonal flowering plants can bring bursts of colour and life to a
              contemporary garden while maintaining its clean, structured look. The key is
              to choose varieties with strong forms, limited colour palettes, and elegant
              simplicity.
            </p>
            <p className={articleBodyStyle}>
              <span className={bold}>Alliums</span> are a favourite in modern designs for
              their bold, spherical blooms and architectural stems. Their geometric shapes
              add height and visual rhythm, working beautifully alongside ornamental
              grasses or minimalist paving. When the flowers fade, their seed heads
              continue to add sculptural interest well into late summer.
            </p>
            <p className={articleBodyStyle}>
              <span className={bold}>Lilies</span> introduce sleek, vertical lines and
              rich texture. Their glossy foliage and refined blooms pair perfectly with
              modern planters or gravel beds. Opt for white or deep burgundy varieties to
              complement neutral, monochrome schemes without overpowering the space.
            </p>
            <p className={articleBodyStyle}>
              <span className={bold}>Salvias</span> bring movement and a soft edge to
              structured layouts. Their vertical spires and silvery foliage contrast
              beautifully with hard surfaces like stone or wood. They’re drought-tolerant,
              long-flowering, and perfect for adding a gentle pop of colour—especially in
              purple, blue, or soft pink tones.
            </p>
            <p className={articleBodyStyle}>
              Other excellent choices include <span className={bold}>Agapanthus</span> for
              its bold blue or white umbels, <span className={bold}>Nepeta</span> for a
              relaxed yet refined texture, and{" "}
              <span className={bold}>Verbena bonariensis</span> for height and airiness
              that won’t disrupt clean lines. Together, these plants offer seasonal
              interest, subtle colour, and sculptural form—all while keeping your garden
              distinctly contemporary and timeless.
            </p>
          </div>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Key Takeaways</h2>
          <ul className={unorderedList}>
            <li>
              Contemporary gardens emphasize clean lines, sharp edges, and minimal
              materials—typically featuring geometric statements and limited colour
              palettes
            </li>
            <li>
              Slotted fence panels and structured hardscaping in materials like concrete,
              timber, and painted brick create the modern backdrop
            </li>
            <li>
              Curves can be incorporated through spheres, round pots, or circular lawns to
              add contrast while maintaining a sharp aesthetic
            </li>
            <li>
              Seating areas and pergolas should follow geometric layouts and use materials
              that blend seamlessly with the garden's overall design
            </li>
            <li>
              Plant selection should be limited to three colours maximum, focusing on
              architectural forms or dense naturalistic foliage
            </li>
            <li>
              Top plant choices include bamboo for screening, ornamental grasses for
              movement, and bold architectural plants like Phormium, Yucca, and Agave
            </li>
            <li>
              Seasonal flowering plants such as Alliums, Lilies, Salvias, and Agapanthus
              add sculptural interest and controlled colour without disrupting clean lines
            </li>
            <li>
              The style suits gardens of any size and balances modern structures with
              natural elements for a timeless, sophisticated look
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style} id="faq">
            FAQ's Building a Contemporary Garden
          </h2>
          <div className={faqGrid}>
            <div>
              <h3 className={faqH3Style}>How much does a contemporary garden cost?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                The cost of designing and building a contemporary garden can vary widely
                depending on materials, features, and whether you use a professional. A
                small DIY contemporary garden might start from around £500–£2,000, while
                professionally designed small gardens can range from £3,000–£8,000. Medium
                gardens often cost between £8,000–£15,000, especially if they include
                features like pergolas, decking, or concrete elements. Large, high-end
                contemporary gardens with custom structures and premium materials can
                exceed £20,000.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                How can I use lighting in my contemporary garden during the evening?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Lighting in a contemporary garden should be sleek, subtle, and
                strategically placed to highlight clean lines and key features. Use
                recessed ground lights along pathways, spotlights to accent architectural
                plants or sculptures, and LED strip lighting under seating or steps for a
                modern, floating effect.{" "}
                <Link href="/garden-design-guide/contemporary/features-of-contemporary-gardens#contemporary-lighting">
                  Read more about lighting here
                </Link>
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                What maintenance does a contemporary garden need to keep looking clean and
                structured?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                To maintain a contemporary garden, regular upkeep is essential to preserve
                its clean, structured look. Key routines include pruning plants to
                maintain their shapes, removing weeds promptly, and sweeping or
                pressure-washing hard surfaces like patios and pathways.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup">Get Started here</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
